import { ChangeDetectorRef, Component, DoCheck, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from "@angular/core";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  ReactionCountItem,
  ReactionMap,
  ReactionType,
  REACTION_EMOJI,
  REACTION_LABELS,
  REACTION_TYPES,
  applyReactionLocally,
  emptyReactionSet,
  getMyReactions,
  getReactionCounts,
  getUserIdsForReaction,
  summaryReactions,
  normalizeReactions,
  setUserReactionsOnDoc,
  UserService,
  avatarColorFor,
  initialsFor,
  profileDisplayName,
} from "@mypxplat/xplat/core";
import { AppService, WebCommunityService } from "@mypxplat/xplat/web/core";
import { SimpleSignupComponent } from "../modals";
import { Subject, Subscription } from "rxjs";

/** How many reactions the summary shows as bubbles. */
const SUMMARY_BUBBLES = 3;
/** Avatars per line of the breakdown before it says "+N". */
const BREAKDOWN_AVATARS = 8;

/** One line of the hover breakdown: a reaction, how many, and who. */
export type ReactionBreakdownRow = {
  type: ReactionType;
  emoji: string;
  label: string;
  count: number;
  /** You left this one — the line is marked. */
  mine: boolean;
  /** The first `BREAKDOWN_AVATARS` reactors; `more` counts the rest. */
  userIds: string[];
  more: number;
};

export type ReactionChangeEvent = {
  comment: any;
  selectedType: ReactionType;
  previousTypes: ReactionType[];
  nextTypes: ReactionType[];
};

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-reaction-control",
  templateUrl: "reaction-control.component.html",
  styleUrls: ["reaction-control.component.scss"],
  standalone: false,
})
export class ReactionControlComponent implements OnInit, OnChanges, DoCheck, OnDestroy {
  private static touchPaletteOpened$ = new Subject<ReactionControlComponent>();

  @Input() comment: any;
  @Output() reactionChange = new EventEmitter<ReactionChangeEvent>();
  @Output() viewProfile = new EventEmitter<string>();

  public reactionTypes = REACTION_TYPES;
  public reactionEmoji = REACTION_EMOJI;
  public reactionLabels = REACTION_LABELS;

  public reactionsMap: ReactionMap = {};
  public myReactions: ReactionType[] = [];
  public myReactionSet: Record<ReactionType, boolean> = emptyReactionSet();
  public reactionCounts: ReactionCountItem[] = [];
  /** The bubbles: the most-used reactions, always including one of yours — see `summaryReactions`. */
  public topReactions: ReactionCountItem[] = [];
  /**
   * Every reaction on the post, most used first, with who left it — the hover breakdown.
   * The bubbles show three; this is where the rest (a lone 👀) can be seen at all.
   */
  public breakdown: ReactionBreakdownRow[] = [];
  /** Every reaction left, across all types — the number beside the bubbles. */
  public reactionTotal: number = 0;
  public summaryLabel: string = "Add a reaction";
  /**
   * The outline smiley in the add bubble. A field rather than an inline array literal in the
   * binding, so the reference is stable across change detection.
   *
   * `far` explicitly: only the REGULAR face-smile is registered, and angular-fontawesome
   * defaults to the `fas` prefix. Solid `face-smile` is a filled disc — it reads as a
   * reaction, not as a button that offers them.
   */
  public smileIcon: IconProp = ["far", "face-smile"];
  public reactionBarOpen: boolean = false;
  /** Palette hangs off the button's right edge instead of its left. See `alignBarWithinViewport`. */
  public barAlignRight: boolean = false;
  /** Same, for the hover breakdown. See `alignReactorsWithinViewport`. */
  public reactorsAlignRight: boolean = false;
  /**
   * When the palette fits on NEITHER side — a container narrower than eleven reactions, like
   * the video player's comment panel — it's pinned to the container's left edge instead
   * (`barLeft`, relative to the button) and narrowed to fit (`barMaxWidth`); it scrolls
   * sideways. Null when one of the two alignments above fits.
   */
  public barLeft: number | null = null;
  public barMaxWidth: number | null = null;
  /** Same, for the hover breakdown. */
  public reactorsLeft: number | null = null;
  public reactorsMaxWidth: number | null = null;
  public focusedReactionIndex: number = 0;

  public shouldShowReactorsList: boolean = false;
  public activeHoveringReactorName: string = "";
  public activeHoveringReactorNameList: Record<string, string> = {};

  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private longPressTriggered: boolean = false;
  private touchInteraction: boolean = false;
  private paletteOpenedByTouch: boolean = false;
  private readonly longPressMs = 450;
  private lastReactionSignature: string = "";
  private closeBarTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly closeBarDelayMs = 200;
  private hideReactorsTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly hideReactorsDelayMs = 200;
  private outsideCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private touchPaletteSub: Subscription | null = null;
  private outsidePointerDown = (event: Event) => {
    if (!this.reactionBarOpen || !this.paletteOpenedByTouch) return;
    const host = this.elementRef.nativeElement as Node;
    const path = typeof (event as any).composedPath === "function" ? (event as any).composedPath() : [];
    if (path.includes(host) || (event.target && host.contains(event.target as Node))) return;
    this.closeReactionBar();
    this.changeDetector.markForCheck();
  };

  constructor(
    public userService: UserService,
    public communityService: WebCommunityService,
    public appService: AppService,
    private changeDetector: ChangeDetectorRef,
    private elementRef: ElementRef
  ) {}

  ngOnInit() {
    this.syncFromComment();
    this.touchPaletteSub = ReactionControlComponent.touchPaletteOpened$.subscribe((opened) => {
      if (opened !== this && this.reactionBarOpen) {
        this.closeReactionBar();
        this.changeDetector.markForCheck();
      }
    });
  }

  ngOnDestroy() {
    this.clearLongPressTimer();
    this.clearCloseBarTimer();
    this.clearHideReactorsTimer();
    this.unbindOutsideCloseListener();
    this.touchPaletteSub?.unsubscribe();
    this.touchPaletteSub = null;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["comment"]) {
      this.syncFromComment();
    }
  }

  ngDoCheck() {
    const signature = this.buildReactionSignature(this.comment);
    if (signature !== this.lastReactionSignature) {
      this.syncFromComment();
    }
  }

  syncFromComment() {
    this.reactionsMap = normalizeReactions(this.comment);
    this.myReactions = getMyReactions(this.reactionsMap, this.userService.user?.id);
    this.myReactionSet = emptyReactionSet();
    for (const type of this.myReactions) {
      this.myReactionSet[type] = true;
    }
    this.reactionCounts = getReactionCounts(this.reactionsMap);
    this.topReactions = summaryReactions(this.reactionCounts, this.myReactions, SUMMARY_BUBBLES);
    this.breakdown = summaryReactions(this.reactionCounts, [], this.reactionCounts.length).map((c) => {
      const ids = getUserIdsForReaction(this.reactionsMap, c.type);
      return {
        type: c.type,
        emoji: c.emoji,
        label: this.reactionLabels[c.type],
        count: c.count,
        mine: this.myReactionSet[c.type],
        userIds: ids.slice(0, BREAKDOWN_AVATARS),
        more: Math.max(0, ids.length - BREAKDOWN_AVATARS),
      };
    });
    this.reactionTotal = this.reactionCounts.reduce((n, c) => n + c.count, 0);
    this.summaryLabel = this.reactionTotal
      ? `${this.reactionTotal} ${this.reactionTotal === 1 ? "reaction" : "reactions"}${this.myReactions.length ? ", including yours" : ""}. Add a reaction`
      : "Add a reaction";
    this.lastReactionSignature = this.buildReactionSignature(this.comment);
    if (this.comment) {
      this.comment.iLike = this.myReactionSet.like;
    }
  }

  private buildReactionSignature(comment: any): string {
    if (!comment) return "";
    return `${JSON.stringify(comment.reactions || {})}|${JSON.stringify(comment.likes || [])}`;
  }

  /**
   * Reactions require an account. For guests, open the sign-in / create-account modal instead of
   * reacting. Returns true when the user is a guest (caller should bail out); `onSuccess` re-runs
   * the intended reaction once they've signed up.
   */
  private promptGuestSignup(onSuccess?: () => void): boolean {
    if (this.userService.user) return false;
    this.appService.promptSimpleSignup(SimpleSignupComponent).then(
      () => onSuccess?.(),
      () => {}
    );
    return true;
  }

  /**
   * The summary OPENS the palette; it never reacts on its own. A click anywhere on it — the +,
   * a bubble or the total — opens it, for mouse and touch alike. On hover only the + opens it
   * (`onAddHover`): hovering a reaction's bubble shows who left it, and the two would fight.
   *
   * No guest prompt here either: opening a palette needs no account, and `selectReaction`
   * already prompts when they actually choose one.
   */
  onMainClick(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.longPressTriggered) {
      this.longPressTriggered = false;
      this.touchInteraction = false;
      return;
    }
    if (this.touchInteraction) {
      this.touchInteraction = false;
      // Only toggle-close if THIS control was already opened via touch.
      // Synthetic mouseenter must not count as "already open" on first tap.
      if (this.reactionBarOpen && this.paletteOpenedByTouch) {
        this.closeReactionBar();
        return;
      }
      this.openReactionBarFromTouch();
      return;
    }
    if (this.reactionBarOpen) {
      this.closeReactionBar();
      return;
    }
    this.openReactionBar();
  }

  onMainKeydown(event: KeyboardEvent) {
    // Enter / Space open the palette rather than reacting, matching the pointer.
    if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      this.openReactionBar();
    }
  }

  onPointerDown(event: Event) {
    if ((event as PointerEvent).pointerType === "mouse") {
      this.touchInteraction = false;
      return;
    }
    this.touchInteraction = true;
    this.clearLongPressTimer();
    this.longPressTriggered = false;
    this.longPressTimer = setTimeout(() => {
      this.longPressTriggered = true;
      this.openReactionBarFromTouch();
      this.changeDetector.markForCheck();
    }, this.longPressMs);
  }

  onPointerUp() {
    this.clearLongPressTimer();
  }

  onPointerLeave() {
    this.clearLongPressTimer();
  }

  /**
   * Hovering the bubbles or the total: the breakdown of every reaction and who left it.
   * Desktop only — a tap fires a synthetic mouseenter, and on a phone that tap is opening the
   * palette. Not while the palette is up, either; the two popups would stack.
   */
  onStackEnter() {
    if (this.touchInteraction || this.reactionBarOpen || !this.breakdown.length) return;
    if (typeof window !== "undefined" && window.matchMedia && !window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
      return;
    }
    this.showBreakdown();
  }

  /** Hovering the + opens the palette, as the old add button did. Desktop only, like the bubbles. */
  onAddHover() {
    if (this.touchInteraction || this.paletteOpenedByTouch || this.reactionBarOpen) return;
    if (typeof window !== "undefined" && window.matchMedia && !window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
      return;
    }
    this.openReactionBar();
  }

  /**
   * The pointer reached the palette — cancel the close that leaving the button scheduled.
   *
   * The bar's `::after` bridges the 6px gap between button and palette; this is the backstop
   * for when it doesn't (a bar that once scrolled sideways clipped its own bridge).
   */
  onBarEnter() {
    this.clearCloseBarTimer();
  }

  /** The pointer reached the reactors list — keep it open. */
  keepReactors() {
    this.clearHideReactorsTimer();
  }

  openReactionBar(focusIndex?: number) {
    this.clearCloseBarTimer();
    this.clearHideReactorsTimer();
    this.shouldShowReactorsList = false;
    // Reset before opening; `alignBarWithinViewport` re-decides from a real measurement.
    this.barAlignRight = false;
    this.barLeft = null;
    this.barMaxWidth = null;
    this.reactionBarOpen = true;
    if (typeof focusIndex === "number") {
      this.focusedReactionIndex = focusIndex;
    } else if (this.myReactions.length) {
      const currentIndex = this.reactionTypes.indexOf(this.myReactions[0]);
      this.focusedReactionIndex = currentIndex >= 0 ? currentIndex : 0;
    } else {
      this.focusedReactionIndex = 0;
    }
    this.alignBarWithinViewport();
  }

  /**
   * Keep the palette on screen.
   *
   * It hangs off the button's LEFT edge and grows rightward, which is right in the detailed
   * view where the control starts the action row. In the community list view the control is
   * right-aligned in its row, and eleven reactions growing rightward from there run off the
   * screen — so flip it to hang off the right edge and grow back over the post.
   *
   * Measured, not estimated. A first attempt computed the width from the option count and
   * got it wrong by the inter-option gaps; rendering first and reading the real rect cannot
   * drift. `detectChanges()` puts the bar in the DOM synchronously and the correction runs
   * in the same task, so the browser only ever paints the final position — no visible flip.
   */
  private alignBarWithinViewport() {
    if (typeof window === "undefined") return;
    this.changeDetector.detectChanges();

    const host = this.elementRef?.nativeElement;
    const bar = host?.querySelector?.(".reaction-control__bar") as HTMLElement | null;
    // `left: 0` / `right: 0` resolve against `__main` — the summary button — so that is what
    // the flip has to be measured against, not the host element.
    const main = host?.querySelector?.(".reaction-control__main") as HTMLElement | null;
    if (!bar || !main) return;

    if (this.overflowsRight(bar, main)) this.barAlignRight = true;
    else if (this.fitsNeitherSide(bar, main)) [this.barLeft, this.barMaxWidth] = this.pinnedPlacement(main);
    else return;
    this.changeDetector.detectChanges();
  }

  /** `[left, maxWidth]` pinning a popup to the clipping container's left edge, relative to `anchor`. */
  private pinnedPlacement(anchor: HTMLElement): [number, number] {
    const MARGIN = 8;
    const bounds = this.clippingBounds(this.elementRef.nativeElement);
    return [Math.round(bounds.left + MARGIN - anchor.getBoundingClientRect().left), Math.max(0, Math.round(bounds.right - bounds.left - MARGIN * 2))];
  }

  /** Spills past the right edge, and hanging it off the button's right edge would spill past the left. */
  private fitsNeitherSide(popup: HTMLElement, anchor: HTMLElement): boolean {
    const MARGIN = 12;
    const bounds = this.clippingBounds(this.elementRef.nativeElement);
    return popup.getBoundingClientRect().right > bounds.right - MARGIN && !this.overflowsRight(popup, anchor);
  }

  /**
   * Whether `popup`, laid out hanging off `anchor`'s LEFT edge, spills past the right edge of
   * the space it has — and whether hanging it off the right edge instead would actually fit.
   *
   * Both popups in this control (the reaction palette and the reactors bubble list) are
   * absolutely positioned at `left: 0` and grow rightward, so both run off screen when the
   * control sits at the right end of a row. This is the one place that decides.
   */
  private overflowsRight(popup: HTMLElement, anchor: HTMLElement): boolean {
    const MARGIN = 12;
    const rect = popup.getBoundingClientRect();
    const bounds = this.clippingBounds(this.elementRef.nativeElement);
    if (rect.right <= bounds.right - MARGIN) return false;
    // Only flip when it actually helps: where the popup is wider than the space available,
    // hanging it off the right edge just moves the overflow to the other side.
    return anchor.getBoundingClientRect().right - rect.width - MARGIN >= bounds.left;
  }

  /** Same treatment for the breakdown that opens off the bubbles. */
  private alignReactorsWithinViewport() {
    if (typeof window === "undefined") return;
    this.reactorsAlignRight = false;
    this.reactorsLeft = null;
    this.reactorsMaxWidth = null;
    this.changeDetector.detectChanges();

    const popup = this.elementRef?.nativeElement?.querySelector?.(".reaction-control__breakdown") as HTMLElement | null;
    // The popup is absolutely positioned against the control's `<li>`, which is its parent.
    const anchor = popup?.parentElement;
    if (!popup || !anchor) return;

    if (this.overflowsRight(popup, anchor)) this.reactorsAlignRight = true;
    else if (this.fitsNeitherSide(popup, anchor)) [this.reactorsLeft, this.reactorsMaxWidth] = this.pinnedPlacement(anchor);
    else return;
    this.changeDetector.detectChanges();
  }

  /**
   * The edges the palette actually has to stay inside.
   *
   * NOT simply the viewport. The community feed scrolls inside its own container, and a
   * palette opened from a right-aligned control gets clipped by that container long before
   * it reaches the window edge. And not just the NEAREST clipping ancestor either: in the
   * video player's comment panel that one is wider than the panel that actually cuts the
   * popup off, so a popup sized to it still spilled — and, being inside a scroller, made the
   * whole panel scroll sideways. So: the tightest edges of EVERY clipping ancestor, and the
   * window's.
   */
  private clippingBounds(host: HTMLElement): { left: number; right: number } {
    let left = 0;
    let right = window.innerWidth;
    let el = host.parentElement;
    while (el && el !== document.body && el !== document.documentElement) {
      const style = window.getComputedStyle(el);
      if (style.overflowX !== "visible" || style.overflowY !== "visible") {
        const box = el.getBoundingClientRect();
        left = Math.max(left, box.left);
        right = Math.min(right, box.right);
      }
      el = el.parentElement;
    }
    return { left, right: Math.max(left, right) };
  }

  private openReactionBarFromTouch(focusIndex?: number) {
    this.paletteOpenedByTouch = true;
    this.openReactionBar(focusIndex);
    ReactionControlComponent.touchPaletteOpened$.next(this);
    this.bindOutsideCloseListener();
  }

  scheduleCloseReactionBar() {
    // Touch opens stay open until a reaction is chosen, the control is toggled, or outside tap.
    if (this.paletteOpenedByTouch || this.touchInteraction) return;
    this.clearCloseBarTimer();
    this.closeBarTimer = setTimeout(() => {
      this.reactionBarOpen = false;
      this.closeBarTimer = null;
      this.changeDetector.markForCheck();
    }, this.closeBarDelayMs);
  }

  onControlMouseLeave() {
    this.scheduleCloseReactionBar();
  }

  closeReactionBar() {
    this.clearCloseBarTimer();
    this.unbindOutsideCloseListener();
    this.reactionBarOpen = false;
    this.paletteOpenedByTouch = false;
  }

  private bindOutsideCloseListener() {
    this.unbindOutsideCloseListener();
    this.outsideCloseTimer = setTimeout(() => {
      document.addEventListener("pointerdown", this.outsidePointerDown, true);
      this.outsideCloseTimer = null;
    }, 0);
  }

  private unbindOutsideCloseListener() {
    if (this.outsideCloseTimer) {
      clearTimeout(this.outsideCloseTimer);
      this.outsideCloseTimer = null;
    }
    document.removeEventListener("pointerdown", this.outsidePointerDown, true);
  }

  private clearCloseBarTimer() {
    if (this.closeBarTimer) {
      clearTimeout(this.closeBarTimer);
      this.closeBarTimer = null;
    }
  }

  onBarKeydown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      event.preventDefault();
      this.closeReactionBar();
      return;
    }
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      this.focusedReactionIndex = (this.focusedReactionIndex + 1) % this.reactionTypes.length;
      return;
    }
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      this.focusedReactionIndex = (this.focusedReactionIndex - 1 + this.reactionTypes.length) % this.reactionTypes.length;
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.selectReaction(this.reactionTypes[this.focusedReactionIndex]);
    }
  }

  selectReaction(selectedType: ReactionType) {
    // Covers reactions chosen from the palette, reaction counts, and keyboard activation.
    if (this.promptGuestSignup(() => this.selectReaction(selectedType))) return;
    const userId = this.userService.user?.id;
    if (!userId) return;
    const previousTypes = [...this.myReactions];
    this.reactionsMap = applyReactionLocally(this.reactionsMap, userId, selectedType);
    const nextTypes = getMyReactions(this.reactionsMap, userId);
    if (this.comment) {
      // `reactions` AND the legacy `likes` together — see `setUserReactionsOnDoc`.
      setUserReactionsOnDoc(this.comment, userId, nextTypes);
      this.comment.iLike = nextTypes.includes("like");
    }
    this.syncFromComment();
    this.closeReactionBar();
    this.reactionChange.emit({
      comment: this.comment,
      selectedType,
      previousTypes,
      nextTypes,
    });
  }

  public reactorPhotoStyles: Record<string, { "background-image": string }> = {};
  /**
   * Initials-fallback for reactors with no resolvable photo.
   *
   * The photo path below guesses a conventional S3 URL for any user we have no
   * cached photo for. That guess 404s for anyone whose avatar never lived there —
   * including every imported Discord author — leaving an empty bubble. These two
   * maps let the template render initials on a deterministic tint instead, matching
   * what comment.component does for post authors.
   */
  public reactorInitials: Record<string, string> = {};
  public reactorColors: Record<string, string> = {};
  public reactorHasPhoto: Record<string, boolean> = {};

  showBreakdown() {
    this.clearHideReactorsTimer();
    this.shouldShowReactorsList = true;
    const userIds = Array.from(new Set(this.breakdown.flatMap((row) => row.userIds)));
    userIds.forEach((userId) => {
      const cached = this.communityService.cachedPhotos[userId];
      // Only trust a photo we actually have. The old unconditional S3 guess produced
      // a broken background-image for everyone else.
      this.reactorHasPhoto[userId] = !!cached;
      if (cached) {
        this.reactorPhotoStyles[userId] = { "background-image": `url(${cached})` };
      }
      this.reactorColors[userId] = avatarColorFor(userId);
      // Imported reactors have no resolvable profile — getUserProfile() cannot look up
      // an `imported:` ghost — so the importer stores their Discord display name on the
      // post as `reactor_names`. Without this fallback every imported bubble shows "?".
      const importedName = this.comment?.reactor_names?.[userId];
      const name = this.communityService.cachedNames[userId] || this.activeHoveringReactorNameList[userId] || importedName;
      if (importedName && !this.activeHoveringReactorNameList[userId]) {
        this.activeHoveringReactorNameList[userId] = importedName;
      }
      this.reactorInitials[userId] = initialsFor(name);
    });
    this.likeUserNames(userIds);
    this.alignReactorsWithinViewport();
  }

  scheduleHideReactors() {
    this.clearHideReactorsTimer();
    this.hideReactorsTimer = setTimeout(() => {
      this.hideReactorsTimer = null;
      this.shouldShowReactorsList = false;
      this.activeHoveringReactorName = "";
      this.changeDetector.markForCheck();
    }, this.hideReactorsDelayMs);
  }

  private clearHideReactorsTimer() {
    if (this.hideReactorsTimer) {
      clearTimeout(this.hideReactorsTimer);
      this.hideReactorsTimer = null;
    }
  }

  reactorMouseEnter(userId: string) {
    this.clearHideReactorsTimer();
    if (this.communityService.cachedNames[userId]) {
      this.activeHoveringReactorName = this.communityService.cachedNames[userId];
      this.activeHoveringReactorNameList[userId] = this.communityService.cachedNames[userId];
    } else {
      this.communityService.getUserProfile(userId).then((result: any) => {
        // getUserProfile resolves without a profile document for deleted / imported / ghost
        // reactors; reading .firstName straight off that undefined result is the TypeError
        // reported to Bugsnag. Guard it and fall back the same way likeUserNames() does —
        // to any imported display name the post carries, then to nothing.
        const name = result ? profileDisplayName(result) : this.comment?.reactor_names?.[userId] || "";
        this.activeHoveringReactorName = name;
        this.activeHoveringReactorNameList[userId] = name;
        this.changeDetector.markForCheck();
      });
    }
  }

  reactorMouseLeave() {
    this.activeHoveringReactorName = "";
  }

  onViewProfile(userId: string) {
    this.viewProfile.emit(userId);
  }

  private likeUserNames(userIds: string[]) {
    userIds.forEach((userId) => {
      // Ghost ids have no profile document; the name came from the post itself above.
      if (String(userId).startsWith("imported:")) return;
      if (this.communityService.cachedNames[userId]) {
        this.activeHoveringReactorNameList[userId] = this.communityService.cachedNames[userId];
      } else {
        this.communityService.getUserProfile(userId).then((result: any) => {
          // Resolves empty for deleted / ghost reactors — the same Bugsnag crash
          // `reactorMouseEnter` guards against, and the breakdown asks for everyone at once.
          if (!result) return;
          const name = profileDisplayName(result);
          this.activeHoveringReactorNameList[userId] = name;
          // Initials only become available once the profile resolves, so refresh them
          // here too — the synchronous pass above has nothing to work with yet.
          this.reactorInitials[userId] = initialsFor(name);
          if (result.photo) {
            this.communityService.cachedPhotos[userId] = result.photo;
            this.reactorPhotoStyles[userId] = { "background-image": `url(${result.photo})` };
            this.reactorHasPhoto[userId] = true;
          }
          this.changeDetector.markForCheck();
        });
      }
    });
  }

  private clearLongPressTimer() {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }
}
